import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
  Menu,
  MenuItem,
  styled,
  alpha,
  useTheme,
  useMediaQuery,
  Tooltip,
  TextField,
  Button,
  Chip,
  Divider,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AssignPayrollType from "./AssignPayrollType";

const ModernTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 500,
  padding: theme.spacing(1.8, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: alpha(theme.palette.primary.main, 0.015),
  },

  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.045),
    transition: "background-color 0.2s ease",
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "600px",
  overflowX: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",

  "&::-webkit-scrollbar": {
    height: "8px",
    width: "8px",
  },

  "&::-webkit-scrollbar-track": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },

  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.25),
    borderRadius: "4px",

    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.4),
    },
  },
}));

function PayrollTypes() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [payrollTypes, setPayrollTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "asc",
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPayrollType, setSelectedPayrollType] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const itemsPerPage = 5;

  const PRIMARY_COLOR = "#0a2e52";
  const SECONDARY_COLOR = "#1565C0";

  useEffect(() => {
    fetchPayrollTypes();
  }, []);

  const fetchPayrollTypes = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/support/payroll-types");
      if (!res.ok) {
        throw new Error(`Failed to fetch payroll types. Status: ${res.status}`);
      }
      const data = await res.json();
      const list = Array.isArray(data)
        ? data
        : data.recordset || data.payrollTypes || [];

      setPayrollTypes(list);
    } catch (error) {
      console.error("Error fetching payroll types:", error);
      setPayrollTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";

    if (sortConfig.key === key) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }

    setSortConfig({
      key,
      direction,
    });
  };

  const filteredPayrollTypes = payrollTypes.filter((item) => {
    const search = searchTerm.toLowerCase().trim();

    if (!search) return true;

    return (
      item.id?.toString().toLowerCase().includes(search) ||
      item.name?.toLowerCase().includes(search) ||
      (item.isActive ? "active" : "inactive").includes(search) ||
      (item.payTPR ? "tpr" : "").includes(search) ||
      (item.payCSR ? "csr" : "").includes(search) ||
      (item.payMedical ? "medical" : "").includes(search) ||
      (item.payMaternity ? "maternity" : "").includes(search) ||
      (item.payCBHI ? "cbhi" : "").includes(search)
    );
  });

  const sortedPayrollTypes = [...filteredPayrollTypes].sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (aValue === null || aValue === undefined) {
      return 1;
    }
    if (bValue === null || bValue === undefined) {
      return -1;
    }

    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
    }

    if (typeof aValue === "boolean" && typeof bValue === "boolean") {
      const comparison = Number(aValue) - Number(bValue);

      return sortConfig.direction === "asc" ? comparison : -comparison;
    }

    const comparison = String(aValue)
      .toLowerCase()
      .localeCompare(String(bValue).toLowerCase());

    return sortConfig.direction === "asc" ? comparison : -comparison;
  });

  const indexOfLastItem = currentPage * itemsPerPage;

  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const currentItems = sortedPayrollTypes.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedPayrollType(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddNew = () => {
    console.log("Add new payroll type");
  };

  const handleEdit = () => {
    if (!selectedPayrollType) return;

    console.log("Edit payroll type:", selectedPayrollType);

    handleMenuClose();
  };

  const handleAssign = () => {
    if (!selectedPayrollType) return;

    setAssignModalOpen(true);
    setAnchorEl(null);
  };

  const BooleanChip = ({ value }) => (
    <Chip
      label={value ? "Yes" : "No"}
      size="small"
      sx={{
        fontWeight: 600,
        minWidth: 48,
        color: value ? "#166534" : "#64748b",
        backgroundColor: value ? "#dcfce7" : "#f1f5f9",
        borderRadius: "6px",
      }}
    />
  );

  const columns = [
    {
      id: "id",
      label: "ID",
      width: "5%",
    },
    {
      id: "name",
      label: "Payroll Type",
      width: "15%",
    },
    {
      id: "isActive",
      label: "Active",
      width: "3%",
    },
    {
      id: "payTPR",
      label: "TPR",
      width: "3%",
    },
    {
      id: "payCSR",
      label: "CSR",
      width: "3%",
    },
    {
      id: "payMedical",
      label: "Medical",
      width: "3%",
    },
    {
      id: "payMaternity",
      label: "Maternity",
      width: "3%",
    },
    {
      id: "payCBHI",
      label: "CBHI",
      width: "3%",
    },
    {
      id: "actions",
      label: "Actions",
      width: "15%",
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        p: {
          xs: 2,
          sm: 3,
        },
        mb: 3,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: {
            xs: "stretch",
            md: "center",
          },
          mb: 3,
          flexDirection: {
            xs: "column",
            md: "row",
          },
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: PRIMARY_COLOR,
            }}
          >
            Payroll Types
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search payroll types..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            sx={{
              width: {
                xs: "100%",
                sm: 280,
              },
            }}
          />

          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{
              minHeight: 40,
              whiteSpace: "nowrap",
              px: 2,
            }}
          >
            Add New
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing <strong>{filteredPayrollTypes.length}</strong>{" "}
          {filteredPayrollTypes.length === 1 ? "payroll type" : "payroll types"}
        </Typography>
      </Box>

      <ResponsiveTableContainer component={Paper} elevation={0}>
        <Table
          size={isMobile ? "small" : "medium"}
          sx={{
            minWidth: 950,
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                bgcolor: theme.palette.primary.light,

                "& th": {
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  whiteSpace: "nowrap",
                  borderBottom: "none",
                  py: 1.8,
                },
              }}
            >
              {columns.map((column) => (
                <ModernTableCell
                  key={column.id}
                  onClick={() => {
                    if (column.id !== "actions") {
                      handleSort(column.id);
                    }
                  }}
                  sx={{
                    width: column.width,
                    cursor: column.id !== "actions" ? "pointer" : "default",

                    "&:hover":
                      column.id !== "actions"
                        ? {
                            backgroundColor: alpha("#fff", 0.08),
                          }
                        : {},
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.8}>
                    {column.label}

                    {sortConfig.key === column.id &&
                      column.id !== "actions" && (
                        <Box
                          display="flex"
                          flexDirection="column"
                          sx={{
                            lineHeight: 0.5,
                          }}
                        >
                          <span
                            style={{
                              fontSize: "0.6rem",
                              opacity: sortConfig.direction === "asc" ? 1 : 0.4,
                            }}
                          >
                            ▲
                          </span>

                          <span
                            style={{
                              fontSize: "0.6rem",
                              opacity:
                                sortConfig.direction === "desc" ? 1 : 0.4,
                            }}
                          >
                            ▼
                          </span>
                        </Box>
                      )}
                  </Box>
                </ModernTableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <ModernTableCell
                  colSpan={9}
                  align="center"
                  sx={{
                    py: 6,
                  }}
                >
                  <CircularProgress size={28} />

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 1.5,
                    }}
                  >
                    Loading payroll types...
                  </Typography>
                </ModernTableCell>
              </TableRow>
            ) : currentItems.length > 0 ? (
              currentItems.map((item) => (
                <StyledTableRow key={item.id} hover>
                  <ModernTableCell>
                    <Chip
                      label={`#${item.id}`}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        color: PRIMARY_COLOR,
                        backgroundColor: alpha(PRIMARY_COLOR, 0.08),
                        borderRadius: "6px",
                      }}
                    />
                  </ModernTableCell>

                  <ModernTableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: PRIMARY_COLOR,
                      }}
                    >
                      {item.name || "—"}
                    </Typography>
                  </ModernTableCell>

                  <ModernTableCell>
                    <BooleanChip value={item.isActive} />
                  </ModernTableCell>

                  <ModernTableCell>
                    <BooleanChip value={item.payTPR} />
                  </ModernTableCell>

                  <ModernTableCell>
                    <BooleanChip value={item.payCSR} />
                  </ModernTableCell>

                  <ModernTableCell>
                    <BooleanChip value={item.payMedical} />
                  </ModernTableCell>

                  <ModernTableCell>
                    <BooleanChip value={item.payMaternity} />
                  </ModernTableCell>

                  <ModernTableCell>
                    <BooleanChip value={item.payCBHI} />
                  </ModernTableCell>

                  <ModernTableCell>
                    <Tooltip title="More actions" arrow>
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, item)}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          color: PRIMARY_COLOR,
                          borderRadius: "8px",

                          "&:hover": {
                            backgroundColor: alpha(PRIMARY_COLOR, 0.08),
                          },
                        }}
                      >
                        <MoreVertIcon
                          fontSize={isMobile ? "small" : "medium"}
                        />
                      </IconButton>
                    </Tooltip>
                  </ModernTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <ModernTableCell
                  colSpan={9}
                  align="center"
                  sx={{
                    py: 7,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                    }}
                  >
                    No payroll types found
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.5,
                    }}
                  >
                    Try changing your search criteria.
                  </Typography>
                </ModernTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTableContainer>

      {filteredPayrollTypes.length > itemsPerPage && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 3,
          }}
        >
          <Pagination
            count={Math.ceil(filteredPayrollTypes.length / itemsPerPage)}
            page={currentPage}
            onChange={(e, value) => setCurrentPage(value)}
            color="primary"
            size={isMobile ? "small" : "medium"}
            sx={{
              "& .MuiPaginationItem-root": {
                "&.Mui-selected": {
                  bgcolor: PRIMARY_COLOR,

                  "&:hover": {
                    bgcolor: SECONDARY_COLOR,
                  },
                },
              },
            }}
          />
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 180,
            borderRadius: "10px",
            boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <MenuItem
          onClick={handleEdit}
          sx={{
            gap: 1.5,
            py: 1.3,
            px: 2,

            "&:hover": {
              backgroundColor: alpha(PRIMARY_COLOR, 0.05),
            },
          }}
        >
          <EditIcon
            fontSize="small"
            sx={{
              color: PRIMARY_COLOR,
            }}
          />

          <Typography variant="body2">Edit</Typography>
        </MenuItem>

        <Divider />
        {/* ASSIGN */}
        <MenuItem
          onClick={handleAssign}
          sx={{
            gap: 1.5,
            py: 1.3,
            px: 2,

            "&:hover": {
              backgroundColor: alpha(SECONDARY_COLOR, 0.06),
            },
          }}
        >
          <AssignmentIcon
            fontSize="small"
            sx={{
              color: SECONDARY_COLOR,
            }}
          />

          <Typography variant="body2">Assign</Typography>
        </MenuItem>
      </Menu>
      <AssignPayrollType
        open={assignModalOpen}
        onClose={() => {
          setAssignModalOpen(false);
          setSelectedPayrollType(null);
        }}
        payrollType={selectedPayrollType}
        onSaveSuccess={fetchPayrollTypes}
      />
    </Box>
  );
}

export default PayrollTypes;
